import React from 'react';
import styled from 'styled-components';
import {
  Text,
  Badge,
  Divider,
  Button,
  Icon,
  Popconfirm,
  Tooltip,
  Space,
} from 'components';
import { useBreakpoint } from 'hooks';
import { AuthContext } from 'contexts';
import { featureEnabled } from 'utils';
import ListingDetailsPreview from './ListingDetailsPreview';
import { ListingFormState, PageView } from '../types';

type SaveListingProps = {
  agreedToTerms?: boolean;
  listingId?: string;
  listing: ListingFormState;
  onSaveNewListing: ({
    moveToNextStep,
    forceDraft,
  }: {
    moveToNextStep?: boolean;
    forceDraft?: boolean;
  }) => void;
  deleteListing: () => Promise<void>;
  restoreListing?: () => Promise<void>;
  setSubmitModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  saved: boolean;
  loading: boolean;
  editing?: boolean;
  isDeleted?: boolean;
  isBeingPublished: boolean;
  currentView?: PageView;
  photos: any[];
  attachments: any[];
  uploading: boolean;
  hasLimitedAccess?: boolean;
};

const SaveListing: React.FC<SaveListingProps> = ({
  agreedToTerms,
  listing,
  listingId,
  onSaveNewListing,
  saved,
  deleteListing,
  restoreListing,
  setSubmitModalOpen,
  loading,
  editing,
  isDeleted,
  isBeingPublished,
  currentView = PageView.Details,
  photos,
  attachments,
  uploading,
  hasLimitedAccess,
}) => {
  const { md } = useBreakpoint();
  const { auth, hasFeature } = React.useContext(AuthContext);
  const canDelete =
    Boolean(listingId) &&
    (featureEnabled('supraHideAdminFunctionalityFeature')
      ? (hasFeature('SUPRA_MVP') &&
          (auth.org.country === 'CAN' || auth.parentOrgType === 'mlsStaff')) ||
        !hasFeature('SUPRA_MVP')
      : true);

  const SaveButton: React.FC<{ onClick?: () => any }> = ({ onClick }) => (
    <Button
      type="primary"
      onClick={onClick}
      disabled={
        loading ||
        uploading ||
        hasLimitedAccess ||
        (!agreedToTerms && listing.pendingVisibility === 'network')
      }
      data-cy="listing-edit-save"
    >
      {editing ? 'Save' : 'Save Draft'}
    </Button>
  );

  const SaveAndPreviewButtons = () => (
    <Space>
      <ListingDetailsPreview
        listing={listing}
        photos={photos}
        attachments={attachments}
        disabled={hasLimitedAccess}
      />
      {!auth.isAdmin && editing && listing.documentStatus === 'uploaded' ? (
        <SaveButton onClick={() => setSubmitModalOpen(true)} />
      ) : (
        <>
          {isBeingPublished ? (
            <Popconfirm
              title="Saving will publish any change to the live listing. Continue?"
              width={230}
              onConfirm={() => onSaveNewListing({ moveToNextStep: false })}
              placement="bottomRight"
            >
              <SaveButton />
            </Popconfirm>
          ) : (
            <SaveButton
              onClick={() => onSaveNewListing({ forceDraft: !editing })}
            />
          )}
        </>
      )}
    </Space>
  );

  return (
    <StyledSaveListingWrapper $md={md}>
      {saved && currentView === PageView.Visibility && (
        <div className="draft-saved">
          <>
            <Badge dot color="success" offset={[0, 2]} />
            <Text type="secondary">{editing ? 'Saved' : 'Draft Saved'}</Text>
          </>
        </div>
      )}
      {isDeleted && (
        <Tooltip title="Restore Listing">
          <Button
            shape="circle"
            icon={<Icon type="undo" width={13} offset={[0, -1]} />}
            onClick={restoreListing}
            disabled={hasLimitedAccess}
          />
        </Tooltip>
      )}
      {canDelete && !isDeleted && (
        <Popconfirm
          title="Are you sure you want to delete this listing?"
          width={230}
          onConfirm={deleteListing}
          placement="bottomRight"
        >
          <Button
            shape="circle"
            icon={
              <Icon
                type="delete"
                width={16}
                color={hasLimitedAccess ? 'disabledColor' : undefined}
              />
            }
            data-cy="delete-listing"
            disabled={hasLimitedAccess}
          />
        </Popconfirm>
      )}
      <div>
        {(saved || canDelete) &&
          (auth.org.country === 'CAN' || auth.parentOrgType === 'mlsStaff') && (
            <Divider type="vertical" />
          )}
      </div>
      {(editing || currentView === PageView.Details) && (
        <SaveAndPreviewButtons />
      )}
    </StyledSaveListingWrapper>
  );
};

export const StyledSaveListingWrapper = styled.div<{
  $md: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  > * {
    margin-right: 15px;
  }
  ${Badge} {
    margin-right: 10px;
  }
  ${Divider} {
    height: 25px;
    margin: 0;
  }
  .draft-saved {
    ${({ $md }) => !$md && 'flex-grow: 1'};
    ${Text} {
      white-space: nowrap;
    }
  }
`;

export default React.memo(SaveListing);
